import type { FC, ReactNode } from 'react'
import type { ListItem } from '@/types'

import { useRef, useState } from 'react'
import { ArrowRight, Clock, Tag } from 'lucide-react'

import {
    motion,
    useMotionValueEvent,
    useScroll,
    useSpring
} from 'motion/react'

import { cn } from '@/lib/utils'
import { arrowClass, buttonClass } from '@/components/generals/button'
import Magnetic from '@/components/generals/magnetic'
import QuoteLink from '@/components/generals/quote-link'
import { howItWorks, quoteLabel } from '@/constants/content'

interface StepProps {
    step: ListItem
    index: number
    reached: boolean
    last?: boolean
}

const Step: FC<StepProps> = ({ step, index, reached, last = false }): ReactNode => {
    return (
        <li className='relative grid grid-cols-[4rem_1fr] gap-5 lg:block'>
            <motion.span
                aria-hidden='true'
                className={cn(
                    'relative z-10 grid size-16 place-items-center rounded-full border-2 border-ink font-heading text-xl font-extrabold transition-colors duration-300',
                    reached ? 'bg-[#21CECD] text-ink' : 'bg-paper text-ink/60'
                )}
                initial={false}
                animate={reached ? { scale: [1, 1.22, 1], rotate: [0, -8, 0] } : { scale: 1, rotate: 0 }}
                transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
            >
                {String(index + 1).padStart(2, '0')}
            </motion.span>

            {last ? <span aria-hidden='true' className='absolute bottom-0 left-0 top-16 z-[5] w-16 bg-paper lg:hidden' /> : null}

            <div className='pb-2 lg:mt-8 lg:pr-6'>
                <h3 className='font-heading text-3xl font-extrabold tracking-[-0.03em] wdth-85 sm:text-4xl'>
                    {step.title}
                </h3>

                <p className='mt-3 max-w-[28rem] text-base leading-relaxed text-ink/75 sm:text-lg'>
                    {step.body}
                </p>
            </div>
        </li>
    )
}

// Four checkpoints on one route. The line fills as you scroll and each step lights up when it is reached.
const Process: FC = (): ReactNode => {
    const listRef = useRef<HTMLOListElement>(null)
    const railY = useRef<HTMLSpanElement>(null)
    const railX = useRef<HTMLSpanElement>(null)
    const [reached, setReached] = useState(-1)
    const steps = howItWorks.steps

    const { scrollYProgress } = useScroll({ target: listRef, offset: ['start 80%', 'end 55%'] })
    const fill = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.4 })

    // A circle lights up the moment the drawn line reaches its edge, measured from the real layout.
    useMotionValueEvent(fill, 'change', (value) => {
        const list = listRef.current
        const horizontal = railX.current && railX.current.offsetWidth > 0
        const rail = horizontal ? railX.current : railY.current

        if (!list || !rail) return

        const line = rail.getBoundingClientRect()
        const length = horizontal ? line.width : line.height
        const circles = Array.from(list.querySelectorAll<HTMLElement>(':scope > li > span:first-child'))
        let next = -1

        if (value > 0.02 && length > 0) {
            circles.forEach((circle, index) => {
                const box = circle.getBoundingClientRect()
                const edge = horizontal ? box.left - line.left : box.top - line.top

                if (value * length >= edge) next = index
            })
        }

        setReached(next)
    })

    return (
        <section id='process' className='relative overflow-x-clip bg-paper py-24 sm:py-32 lg:py-40'>
            <div className='shell'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-10'>
                    <h2 className='font-heading text-[16vw] font-bold leading-[0.86] tracking-[-0.015em] wdth-78 sm:text-[12vw] lg:col-span-7 lg:text-[8.4vw] 2xl:text-[7.75rem]'>
                        {howItWorks.heading}
                    </h2>

                    <p className='max-w-[30rem] text-lg leading-relaxed text-ink/75 sm:text-xl lg:col-span-4 lg:col-start-9 lg:pb-3'>
                        {howItWorks.intro}
                    </p>
                </div>

                <ol ref={listRef} className='relative mt-16 grid gap-12 lg:mt-24 lg:grid-cols-4 lg:gap-8'>
                    <span ref={railY} aria-hidden='true' className='absolute bottom-0 left-[calc(2rem_-_1.5px)] top-0 w-[3px] rounded-full bg-ink/10 lg:hidden'>
                        <motion.span style={{ scaleY: fill }} className='absolute inset-0 origin-top rounded-full bg-[#21CECD]' />
                    </span>

                    <span ref={railX} aria-hidden='true' className='absolute left-0 right-[calc((100%_-_6rem)/4_-_2rem)] top-[calc(2rem_-_1.5px)] hidden h-[3px] rounded-full bg-ink/10 lg:block'>
                        <motion.span style={{ scaleX: fill }} className='absolute inset-0 origin-left rounded-full bg-[#21CECD]' />
                    </span>

                    {steps.map((step, index) => (
                        <Step key={step.title} step={step} index={index} reached={reached >= index} last={index === steps.length - 1} />
                    ))}
                </ol>

                <div className='mt-16 grid gap-8 border-t-2 border-ink pt-10 lg:mt-24 lg:grid-cols-12 lg:items-center lg:gap-10'>
                    <p className='flex items-center gap-4 text-lg leading-snug sm:text-xl lg:col-span-5'>
                        <Clock aria-hidden='true' className='size-6 shrink-0 text-blaze-deep' />
                        {howItWorks.timeline}
                    </p>

                    <p className='flex items-center gap-4 text-lg leading-snug sm:text-xl lg:col-span-4'>
                        <Tag aria-hidden='true' className='size-6 shrink-0 text-blaze-deep' />
                        {howItWorks.price}
                    </p>

                    <div className='lg:col-span-3 lg:flex lg:justify-end'>
                        <Magnetic className='w-full sm:w-auto'>
                            <QuoteLink className={buttonClass({ size: 'lg', className: 'w-full' })}>
                                {quoteLabel}
                                <ArrowRight aria-hidden='true' className={arrowClass} />
                            </QuoteLink>
                        </Magnetic>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Process
